/**
 * Robust Clipboard Utility
 * Handles clipboard operations with multiple fallbacks for browser compatibility
 * and permissions issues
 */

export async function copyToClipboard(text: string): Promise<boolean> {
  // Method 1: Modern Clipboard API (requires secure context and permissions)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      // Silently fall through to fallback methods
      // No need to log - this is expected in many environments
    }
  }

  // Method 2: Legacy execCommand (deprecated but widely supported)
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    // Make the textarea invisible
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      return true;
    }
  } catch (err) {
    // Silently continue to final fallback
  }

  // Method 3: Final fallback - create a temporary input and prompt user to copy
  try {
    const input = document.createElement('input');
    input.value = text;
    input.style.position = 'fixed';
    input.style.top = '50%';
    input.style.left = '50%';
    input.style.transform = 'translate(-50%, -50%)';
    input.style.zIndex = '9999';
    input.style.padding = '10px';
    input.style.border = '2px solid #6366f1';
    input.style.borderRadius = '8px';
    input.style.fontSize = '14px';
    input.style.backgroundColor = 'white';
    
    document.body.appendChild(input);
    input.focus();
    input.select();
    
    // Wait for user to manually copy
    return new Promise((resolve) => {
      const cleanup = () => {
        document.body.removeChild(input);
      };
      
      // Auto-cleanup after 5 seconds
      setTimeout(() => {
        cleanup();
        resolve(false);
      }, 5000);
      
      // Cleanup on blur (user clicked away)
      input.addEventListener('blur', () => {
        cleanup();
        resolve(true); // Assume they copied
      });
    });
  } catch (err) {
    console.error('All clipboard methods failed:', err);
    return false;
  }
}

/**
 * Synchronous version - tries only execCommand
 * Use this when you can't use async/await
 */
export function copyToClipboardSync(text: string): boolean {
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.opacity = '0';
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    return successful;
  } catch (err) {
    console.error('Sync clipboard copy failed:', err);
    return false;
  }
}

/**
 * Check if clipboard API is available
 */
export function isClipboardAvailable(): boolean {
  return !!(navigator.clipboard && window.isSecureContext);
}